import { css } from '@emotion/core'
import { Arrow, ContentAttach, DocumentBasic, NoticeLoading, ThingHandwriting } from '@fs/zion-icon'
import { Highlights, SingleImageViewer, RecordHighlight, useHighlights } from '@fs/zion-image-viewer'
import { useEvent } from '@fs/zion-frontend-friends'
import { useQueryParams } from '@fs/zion-router'
import { Title } from '@fs/zion-head'
import { SocialMenu } from '@fs/zion-social'
import {
  IconButton,
  Row,
  Separator,
  useOverlay,
  useZoomPan,
  useContainerWidth,
  Button,
  Card,
  usePageWidth,
  H1,
  Subheading,
  LabelSpacer,
  Cell,
} from '@fs/zion-ui'
import ZionForm, { useZionForm, FormTextField } from '@fs/zion-form'
import React, { Suspense, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { usePageView, trackEvent } from '@fs/zion-analytics'
import { Usabilla } from '@fs/zion-usabilla'
import { useFeatureFlag } from '@fs/zion-flags'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import HandwritingOverlay from '../../components/handwriting/HandwritingOverlay'
import {
  saveEdit,
  saveGetInvolvedStats,
  taskCompleted,
} from '../../../shared/services/recordEnhancement/recordEnhancementService'
import ErrorOverlay from '../../../shared/components/errorOverlay/ErrorOverlay'
import { getZoom, getPan } from '../../utils/zoomPanUtils'
import Navigation from './components/navigation/Navigation'
import HiddenMenu from './components/hiddenMenu/HiddenMenu'
import useQuickNameTasks from './hooks/useQuickNameTasks'
import { setDisplayHighlights } from '../../utils/imageHighlightUtils'
import TasksDoneOverlay from '../../components/sharedReviewComponents/tasksDoneOverlay/TasksDoneOverlay'
import { goBackToGive } from '../../utils/reviewUtils'
import ReviewCompletionDialog from '../../components/sharedReviewComponents/ReviewCompletionDialog/ReviewCompletionDialog'
import { useFlaggedUser } from '../../../Give/utils/flaggedUserLibrary/FlaggedUserContext'
import ConfirmNotAPersonOverlay from '../../components/sharedReviewComponents/confirmNotAPersonOverlay/ConfirmNotAPersonOverlay'
import FeedbackOverlay from '../../../shared/components/feedbackOverlay/FeedbackOverlay'
import usePrisonAccess from '../../../Give/hooks/usePrisonAccess'
import { filterIllegalReviewFieldCharacters } from '../../../shared/utils/utils'
import useSkipImageOverlay from '../../components/sharedReviewComponents/skipImageOverlay/useSkipImageOverlay'
import SkipImageOverlay from '../../components/sharedReviewComponents/skipImageOverlay/SkipImageOverlay'

const layoutCss = css`
  display: flex;
  flex-direction: column;
  // This fixes the header and the bottom docked infosheet covering the image viewer
  flex-grow: 1;
  /* Keep navigation anchored at bottom while image viewer loads */
  justify-content: space-between;
  /* Prevent overflow due to negative margins */
  overflow: hidden;
  /* Temporary hack to hide the outline on highlights */
  [class^='highlightCss'] {
    box-shadow: none !important;
  }

  /* Temporary hack to shift the QuickGlanceOverlay down */
  [class^='quickGlanceBleedableCss'] {
    margin-top: 20px;
  }
`

// allow the Edit button to give focus to the text field
const useFocus = () => {
  const htmlElRef = useRef(null)
  const setFocus = useCallback(() => {
    htmlElRef.current && htmlElRef.current.focus()
  }, [])
  return [htmlElRef, setFocus]
}

const NameCorrectionSelectedContent = ({ latestName, handleSubmit, childInputRef }) => {
  const [t] = useTranslation()
  const shape = {
    nameField: Yup.string().test(
      'contains-illegal-characters',
      t('task-viewer.error.illegal-characters', "You can only enter ' - , . diacritic, and alphanumeric characters."),
      (value) => value === filterIllegalReviewFieldCharacters(value)
    ),
  }
  const schema = Yup.object().shape(shape)
  const methods = useZionForm({
    mode: 'all',
    defaultValues: {
      nameField: filterIllegalReviewFieldCharacters(latestName),
    },
    resolver: yupResolver(schema),
  })
  return (
    <Card>
      <ZionForm onSubmit={handleSubmit} methods={methods} id="name-correction-selected-content">
        <Row gutters="xxs" alignX="center">
          <FormTextField
            data-testid="name-text-field"
            name="nameField"
            ref={childInputRef}
            resettable
            label={t('task-viewer.name-correction-selected-content.name-field-label', 'Name')}
          />
          {methods.formState.isDirty && (
            <Cell noShrinkX>
              <LabelSpacer />
              <Button
                size="medium"
                emphasis="high"
                form="name-correction-selected-content"
                type="submit"
                linkName="Submit Edit"
                data-testid="Submit"
                onTouchStart={() => {
                  handleSubmit(methods.getValues())
                }}
              >
                {t('task-viewer.save-btn', 'Submit')}
              </Button>
            </Cell>
          )}
        </Row>
      </ZionForm>
    </Card>
  )
}

const QuickNameReview = () => {
  usePageView({ page_channel: 'RIP', page_detail: 'Name Corrections' })
  const feedbackFlag = useFeatureFlag('rip_sharedImageViewer_feedback_NameCorrections')
  const { query } = useQueryParams()
  // New Params: action, task: taskParam, surname, placeName e.g. action=EnhanceImageRecords&task=Name%20Correction&surname=Schwantes&placeName=Texas
  const { action, placeCode: placeCodeStr, language: recordLanguage, surname, debugArk } = query
  // eslint-disable-next-line
  const placeCode = parseInt(placeCodeStr, 10);
  const [t] = useTranslation()
  const atWidth = useContainerWidth()
  const isSmall = atWidth({ default: true, md: false })
  const pageWidth = usePageWidth()
  const isMobile = pageWidth({ default: true, md: false })
  const { checkForFlaggedBehavior } = useFlaggedUser()

  // / Local state / //
  const [childInputRef, setInputFocus] = useFocus()
  const [error, setError] = useState(false) // Whether or not to show the error dialog
  const [loading, setLoading] = useState('')
  const [taskErrorMessage, setTaskErrorMessage] = useState()

  // / Zion overlays and refs / //
  const finishedDialogOverlay = useOverlay()
  const handwritingOverlay = useOverlay()
  const confirmNotAPersonOverlay = useOverlay()
  const [{ controllerRef, stageRef, viewerRef }, { set: setZoomPan }] = useZoomPan()
  const sharable = { url: 'https://www.familysearch.org/getinvolved' } // Share object
  const [highlightsState, highlightsApi] = useHighlights()

  const { accessFromPrison, isReady } = usePrisonAccess()

  const {
    goToPreviousTask,
    goToNextTask,
    handleContinue,
    updateNameCorrectionTaskValue,
    showRecordDetailsAndAttachToTree,
    handleClickRecordDetails,
    handleClickAttachToTree,
    doneCount,
    currentTask,
    imageAuth,
    loading: loadingTasks,
    error: loadingError,
    disableBackButton,
    maxTasks,
    index,
    getNewImage,
  } = useQuickNameTasks({
    debugArk,
    placeCode,
    recordLanguage,
    action,
    surname,
    finishedDialogOverlay,
    setZoomPan: () => {},
    setHighlights: () => {},
  })

  const { handleSkip, handleClickNewImage, resetSkipCounter, skipImageOverlay } = useSkipImageOverlay({
    onSkip: goToNextTask,
    onGetNewImage: getNewImage,
    currentTask,
  })

  const getLatestFieldValue = (task) => {
    const newFieldValues = task?.fieldValues
    const latestName = newFieldValues?.[newFieldValues.length - 1]?.origValue?.text
    return typeof latestName === 'string' ? latestName : ''
  }

  const handleSave = useEvent(async (editedValue, newFieldType = '') => {
    const isNotAPerson = newFieldType === 'WORD'
    if (isNotAPerson) {
      trackEvent({
        event_name: 'click_action',
        link_name: `Not A Person`,
      })
    }

    try {
      setLoading(true)
      const { partitionKey, id } = currentTask
      // Filter out illegal characters from the latest value.
      // This ensures any illegal characters saved in the database are removed before saving the edit.
      // If the database has illegal characters, the user won't see them, and will potentially click "match" if all the rest of the legal characters match.
      const newValue = editedValue || filterIllegalReviewFieldCharacters(getLatestFieldValue(currentTask))
      const isFixed = !!editedValue
      if (isNotAPerson) currentTask.oldFieldType = currentTask.fieldType
      await saveEdit({
        edit: newValue,
        id,
        isFixed,
        newFieldType,
        partitionKey,
        placeRepId: placeCode,
      })
      saveGetInvolvedStats({
        partitionKey,
        taskType: 'Name Correction',
        // eslint-disable-next-line no-nested-ternary
        action: isNotAPerson ? 'not-a-person' : isFixed ? 'fixed' : 'reviewed',
      })
      if (!isNotAPerson) {
        checkForFlaggedBehavior({
          ...currentTask,
          actionType: 'edit',
          ark: query.autoGenImageArk,
          editedValue,
          originalValue: getLatestFieldValue(currentTask),
        })
        if (editedValue) updateNameCorrectionTaskValue(editedValue)
      }
      setLoading(false)
      goToNextTask({ justMarkedNotAPerson: isNotAPerson })
    } catch (saveError) {
      // eslint-disable-next-line no-console
      console.warn('Error saving edit', saveError)
      setLoading(false)
      setError(true)
    }
  })

  const goBack = async () => {
    if (!loading && currentTask) await taskCompleted(currentTask.apid, false, 'Name Correction')
    goBackToGive(accessFromPrison && isReady, 'quickNameReview')
  }

  const handleClickNotAPerson = useCallback(() => {
    confirmNotAPersonOverlay.handleClick()
  }, [confirmNotAPersonOverlay])

  const handleClickConfirmNotAPerson = () => {
    confirmNotAPersonOverlay.close()
    handleSave({}, 'WORD')
    checkForFlaggedBehavior({
      ...currentTask,
      actionType: 'notAPerson',
      ark: query.autoGenImageArk,
      originalValue: getLatestFieldValue(currentTask),
      timestamp: new Date(),
    })
  }

  const convertSlsRectsToHighlights = (rects) => {
    if (!rects || rects.length === 0) return []
    return rects?.map((r) => ({
      id: Math.random(),
      position: {
        x: r.x1,
        y: r.y1,
        width: r.x2 - r.x1,
        height: r.y2 - r.y1,
      },
    }))
  }

  // / Effects / //
  useEffect(() => {
    setDisplayHighlights(convertSlsRectsToHighlights(currentTask.rects), highlightsApi)
  }, [currentTask, highlightsApi])

  useEffect(() => {
    if (highlightsState.ids.length > 0) {
      highlightsApi.select(highlightsState.ids[0])
    }
  }, [highlightsApi, highlightsState])

  useEffect(() => {
    if (currentTask && currentTask.rects) {
      const { panX, panY } = getPan([currentTask.rects[0]])
      const zoom = getZoom(currentTask.rects, isSmall)
      setZoomPan({ zoom, panX, panY })
    }
  }, [currentTask, isSmall, setZoomPan])

  const counterHeading = maxTasks
    ? t('task-viewer.counter-with-total', `(${doneCount + 1} of ${maxTasks})`, {
        counter: doneCount + 1,
        total: maxTasks,
      })
    : `(${doneCount + 1})`

  const getTitle = () => `${t('task-viewer.quick-name-review-title', 'Quick Name Review')} ${counterHeading}`

  if (/NoMoreTasks/.test(loadingError?.message)) {
    if (!taskErrorMessage) {
      setTaskErrorMessage({
        title: t('tasks-done-overlay.no-more-tasks.error.title', 'No more tasks'),
        message: t('tasks-done-overlay.tasks-done.error.message', 'Go back to Get Involved to do another search.'),
      })
    }
  }

  if (/TaskExpired/.test(loadingError?.message)) {
    if (!taskErrorMessage) {
      setTaskErrorMessage({
        title: t('tasks-done-overlay.tasks-done.error.title', 'Task has expired'),
        message: t('tasks-done-overlay.tasks-done.error.message', 'Go back to Get Involved to do another search.'),
      })
    }
  }

  const SelectedContent = useCallback(
    () => (
      <NameCorrectionSelectedContent
        latestName={getLatestFieldValue(currentTask)}
        handleSubmit={({ nameField }) => handleSave(nameField)}
        childInputRef={childInputRef}
      />
    ),
    [childInputRef, currentTask, handleSave]
  )

  // Update save handler to reset skip counter
  const handleSaveWithReset = useCallback(
    (...args) => {
      resetSkipCounter()
      handleSave(...args)
    },
    [handleSave, resetSkipCounter]
  )

  // Update not-a-person handler to reset counter
  const handleClickNotAPersonWithReset = useCallback(() => {
    resetSkipCounter()
    handleClickNotAPerson()
  }, [handleClickNotAPerson, resetSkipCounter])

  return (
    <Suspense fallback={<NoticeLoading />}>
      <Title>{t('task-viewer.quick-name-review-title', 'Quick Name Review')}</Title>
      <div css={layoutCss}>
        <Separator size="nano" />
        <Row alignY="middle">
          <Separator />
          <IconButton
            Icon={Arrow}
            iconDirection="backward"
            onClick={() => {
              trackEvent({
                event_name: 'click_action',
                link_name: `To GIVE: Top Left`,
              })
              goBack()
            }}
            title={t('task-viewer.get-involved-link', 'Back to Get Involved')}
            disabled={loading || loadingTasks}
          />
          {atWidth({
            default: (
              <H1 size="H5" growX>
                {t('task-viewer.quick-name-review-title', 'Quick Name Review')}
                <Subheading>{counterHeading}</Subheading>
              </H1>
            ),
            sm: (
              <H1 size="H5" growX>
                {getTitle()}
              </H1>
            ),
          })}
          {imageAuth &&
            !accessFromPrison &&
            isReady &&
            showRecordDetailsAndAttachToTree &&
            (isMobile ? (
              <IconButton
                disabled={loading}
                Icon={DocumentBasic}
                onClick={() => handleClickRecordDetails()}
                data-testid="record-details"
                linkName="Record Details"
                title={t('navigation.details-button', 'Record Details')}
              />
            ) : (
              <Button
                disabled={loading}
                Icon={DocumentBasic}
                onClick={() => handleClickRecordDetails()}
                data-testid="record-details"
                linkName="Record Details"
              >
                {t('navigation.details-button', 'Record Details')}
              </Button>
            ))}
          {imageAuth &&
            !accessFromPrison &&
            isReady &&
            showRecordDetailsAndAttachToTree &&
            (isMobile ? (
              <IconButton
                disabled={loading}
                Icon={ContentAttach}
                onClick={() => handleClickAttachToTree()}
                data-testid="attach-to-tree"
                linkName="Attach To Tree"
                title={t('attach.header', 'Attach To Tree')}
              />
            ) : (
              <Button
                emphasis="medium"
                disabled={loading}
                Icon={ContentAttach}
                onClick={() => handleClickAttachToTree()}
                data-testid="attach-to-tree"
                linkName="Attach To Tree"
              >
                {t('attach.header', 'Attach To Tree')}
              </Button>
            ))}
          {atWidth({
            sm: (
              <IconButton
                Icon={ThingHandwriting}
                title={t('task-viewer.handwriting-tooltip', 'Handwriting Examples')}
                onClick={() => {
                  trackEvent({
                    event_name: 'click_action',
                    link_name: `Handwriting Help`,
                  })
                  handwritingOverlay.handleClick()
                }}
              />
            ),
          })}
          {!accessFromPrison && isReady && <SocialMenu useIconButton sharable={sharable} data-testid="social-share" />}
          {atWidth({
            default: (
              <HiddenMenu
                handleClickRecordDetails={handleClickRecordDetails}
                handleClickAttachToTree={handleClickAttachToTree}
                handleSave={handleSaveWithReset}
                imageAuth={imageAuth}
                showNotAPerson={false}
                showRecordDetails
              />
            ),
            sm: <div />,
          })}
        </Row>
        <Separator size="nano" />
        <ErrorOverlay error={error} setError={setError} />
        {currentTask && currentTask.artifactUrl && (
          <SingleImageViewer
            {...{ controllerRef, stageRef, viewerRef }}
            alt=""
            src={currentTask.artifactUrl}
            maxZoom={20}
            initialZoom={getZoom(currentTask.rects, isSmall)}
            initialPanX={getPan([currentTask.rects[0]]).panX}
            initialPanY={getPan([currentTask.rects[0]]).panY}
          >
            <Highlights
              state={highlightsState}
              api={highlightsApi}
              Component={RecordHighlight}
              SelectedContent={SelectedContent}
            />
            <ReviewCompletionDialog
              dialogOverlay={finishedDialogOverlay}
              initialChipValue="20names"
              chipOptions={[
                {
                  label: t('task-viewer.finished-modal.option.20names', '20 Names'),
                  value: '20names',
                  max: 20,
                  selected: true,
                },
                {
                  label: t('task-viewer.finished-modal.option.30names', '30 Names'),
                  value: '30names',
                  max: 30,
                },
                {
                  label: t('task-viewer.finished-modal.option.50names', '50 Names'),
                  value: '50names',
                  max: 50,
                },
                {
                  label: t('task-viewer.finished-modal.option.nolimit', 'No limit'),
                  value: 'nolimit',
                  max: null,
                },
              ]}
              totalFinished={doneCount + 1}
              handleContinue={handleContinue}
              goBackToGive={goBack}
              placeCode={placeCode}
              name={getLatestFieldValue(currentTask)}
            />
            <ErrorOverlay />
          </SingleImageViewer>
        )}
        <Separator size="xs" />
        <Navigation
          disableBackButton={disableBackButton}
          atWidth={atWidth}
          goToNextTask={handleSkip}
          goToPreviousTask={goToPreviousTask}
          handleSave={handleSaveWithReset}
          handleClickNotAPerson={handleClickNotAPersonWithReset}
          focusOnEditField={setInputFocus}
          index={index}
          loading={loading || loadingTasks}
        />
        <HandwritingOverlay {...handwritingOverlay} />
        <ConfirmNotAPersonOverlay
          overlay={confirmNotAPersonOverlay}
          handleClickConfirmNotAPerson={handleClickConfirmNotAPerson}
        />
        {taskErrorMessage && (
          <TasksDoneOverlay title={taskErrorMessage.title} message={taskErrorMessage.message} goBackToGive={goBack} />
        )}

        <SkipImageOverlay overlay={skipImageOverlay} handleClickNewImage={handleClickNewImage} />
      </div>
      {!isMobile && <Usabilla url="//w.usabilla.com/fcf53049fea9.js" hideOnMobile mobileBreakpoint="md" />}
      {!isMobile && feedbackFlag.isOn && <FeedbackOverlay />}
    </Suspense>
  )
}

export default QuickNameReview
